import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Bell, RefreshCw, Menu, ChevronDown, User, LogOut, Settings } from 'lucide-react'
import { useUI } from '@/lib/store'
import { useAuth } from '@/hooks/useAuth'
import { getRoleInfo } from '@/lib/constants'
import FilterPanel from '@/components/shared/FilterPanel'

// =============================================================================
// Page title mapping
// =============================================================================

const PAGE_TITLES: Record<string, string> = {
  '/':           'Dashboard',
  '/map':        'Mapa de Planta',
  '/production': 'Seguimiento de Producción',
  '/documents':  'Gestión de Documentos',
  '/validation': 'Cola de Validaciones',
  '/reports':    'Informes y Exportaciones',
  '/admin':      'Administración',
}

// =============================================================================
// TopBar Component
// =============================================================================

export default function TopBar() {
  const location                                 = useLocation()
  const { notifications, setSidebarOpen, sidebarOpen } = useUI()
  const { user, logout }                         = useAuth()
  const [userMenuOpen, setUserMenuOpen]          = useState(false)
  const [filterPanelOpen, setFilterPanelOpen]    = useState(false)

  const pageTitle = PAGE_TITLES[location.pathname] ?? 'PSFV Navabuena'
  const roleInfo  = user ? getRoleInfo(user.role) : null

  const showFilters = ['/production', '/map', '/documents', '/validation'].includes(
    location.pathname
  )

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center px-6 gap-4 shrink-0 z-10">
      {/* Mobile hamburger */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Page title */}
      <div className="flex-1 min-w-0">
        <h1 className="text-lg font-semibold text-gray-900 truncate">{pageTitle}</h1>
        <p className="text-xs text-gray-400">PSFV Navabuena — EPC Tracker</p>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-3">
        {/* Filter toggle (context-aware) */}
        {showFilters && (
          <div className="relative">
            <button
              onClick={() => setFilterPanelOpen(!filterPanelOpen)}
              className="btn-secondary text-xs gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filtros
              <ChevronDown className={`w-3 h-3 transition-transform ${filterPanelOpen ? 'rotate-180' : ''}`} />
            </button>

            {filterPanelOpen && (
              <div className="absolute right-0 top-full mt-2 z-50 w-80">
                <FilterPanel onClose={() => setFilterPanelOpen(false)} />
              </div>
            )}
          </div>
        )}

        {/* Refresh button */}
        <button
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          onClick={() => window.location.reload()}
          title="Actualizar datos"
        >
          <RefreshCw className="w-4 h-4" />
        </button>

        {/* Notifications */}
        <button className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors">
          <Bell className="w-5 h-5" />
          {notifications > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {notifications > 9 ? '9+' : notifications}
            </span>
          )}
        </button>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-brand-600 flex items-center justify-center text-xs font-bold text-white">
                {user?.full_name?.slice(0, 2).toUpperCase() ?? 'U'}
              </div>
            )}
            <div className="hidden sm:block text-left min-w-0">
              <p className="text-sm font-medium text-gray-800 leading-tight truncate max-w-[120px]">
                {user?.full_name ?? 'Usuario'}
              </p>
              {roleInfo && (
                <p className="text-xs leading-tight" style={{ color: roleInfo.color }}>
                  {roleInfo.shortLabel}
                </p>
              )}
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown */}
          {userMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setUserMenuOpen(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-panel z-50 overflow-hidden">
                {/* User info */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">
                    {user?.full_name ?? 'Usuario'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {user?.company?.name ?? 'Sin empresa'}
                  </p>
                </div>

                {/* Menu items */}
                <div className="py-1">
                  <button
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <User className="w-4 h-4 text-gray-400" />
                    Mi Perfil
                  </button>
                  {user?.role === 'ADMIN' && (
                    <button
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4 text-gray-400" />
                      Configuración
                    </button>
                  )}
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={() => { setUserMenuOpen(false); logout() }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
